import { useEffect, useState } from 'react';
import { FormLayout, Modal, Select, Spinner, Checkbox, Banner, TextContainer, List, Box } from '@shopify/polaris';
import { formatToCurrency, getRequest } from '../../../shared/components/functions';
import CompanySelect from '../../../shared/components/companySelect';

export function JobTransformerModal({ setJob, setInvoicedForHoreca, invoicedForHoreca, active, setActive, handleJobTransform, company, resource }) {
  const [selectedResource, setSelectedResource] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    async function getResources() {
      //this will get all the invoices/quotations from the invoicing address company and set it to the invoices/quotations
      const data = await getRequest(`/api/${resource}s?limit=20&FIRNR=${company.FirNrFactuur || company.FIRNR}`);
      if (Array.isArray(data)) {
        let optionsArr = [];
        if (resource === 'invoice') optionsArr = data.filter((r) => r.status === 0).map((r) => ({ label: `${r.Factuurnummer} - ${formatToCurrency(r.totaalbedrag)}`, value: r.faktuurnr }));
        if (resource === 'quotation') optionsArr = data.map((r, idx) => ({ key: idx, label: `${r.Offertenummer} - ${formatToCurrency(r.price)}`, value: r.offertenr }));
        setOptions([{ label: `Select ${resource}...`, value: null }, ...optionsArr]);
      }
      setLoaded(true);
    }
    if (active) getResources();
  }, [company, active]);

  const handleCompanyChange = async (FIRNR, id) => {
    setJob((job) => ({ ...job, [id]: FIRNR }));
  };

  return (
    <Modal
      open={active}
      onClose={() => {
        setActive(false);
        setLoaded(false);
        setOptions([]);
      }}
      title={resource === 'invoice' ? 'Invoice job' : 'Make quotation to create pro forma invoice'}
      primaryAction={{ content: `Make new ${resource}`, onAction: () => handleJobTransform(false, null, `${resource}s`) }}
      secondaryActions={{ content: `Append to selected ${resource}`, onAction: () => handleJobTransform(true, selectedResource, `${resource}s`), disabled: !selectedResource }}
      size="large"
    >
      <Modal.Section>
        {!loaded ? (
          <Spinner size="small" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '2rem' }}>
            {/* Left Column: Form */}
            <div style={{ flex: '1 1 40%', minWidth: '300px' }}>
              <FormLayout>
                <CompanySelect value={company.FIRNR} id="CustomerId" onChange={handleCompanyChange} label={`Select customer for ${resource}:`} isDisabled={false} isClearable />
                <Select label={`Append job to existing ${resource}:`} value={parseInt(selectedResource) || ''} onChange={setSelectedResource} options={options} />
              </FormLayout>

              {/* switcher to indicate that this needs to be invoiced for horeca  */}
              <div
                style={{ marginTop: '2rem', cursor: 'pointer' }}
                onClick={(e) => {
                  if (e.target.type !== 'checkbox') {
                    setInvoicedForHoreca(!invoicedForHoreca);
                  }
                }}
              >
                <Banner title="Horeca Invoice" status="warning">
                  <p style={{ marginBottom: '10px' }}>
                    <strong>Is this invoice for a Horeca customer?</strong>
                  </p>
                  <Checkbox label="Invoice for horeca" checked={invoicedForHoreca} onChange={(value) => setInvoicedForHoreca(value)} />
                </Banner>
              </div>
            </div>

            {/* Right Column: Explanation */}
            <div style={{ flex: '1 1 60%', borderLeft: '1px solid #dfe3e8', paddingLeft: '2rem' }}>
              <TextContainer>
                <h3 style={{ marginTop: 0 }}>How is the invoice calculated?</h3>
                <p>The system automatically calculates the invoice based on the following business rules:</p>

                <p>
                  <strong>1. Tariff Selection Logic</strong>
                </p>
                <p>The hourly rate is determined by checking these conditions in order:</p>
                <List type="number">
                  <List.Item>
                    <strong>IT Tariff:</strong> If the technician is flagged as an IT specialist, the IT rate applies regardless of the machine.
                  </List.Item>
                  <List.Item>
                    <strong>Maintenance Contract (OHC):</strong> If a contract exists, the rate depends on the machine brand:
                    <List type="bullet">
                      <List.Item>
                        <strong>KT Rate:</strong> Applied for <em>Probat</em> machines.
                      </List.Item>
                      <List.Item>
                        <strong>DCM Rate:</strong> Applied for <em>Mahlkönig</em>, <em>Dalla Corte</em>, and <em>Kvdw</em>.
                      </List.Item>
                      <List.Item>
                        <strong>VTOC Rate:</strong> Applied for <em>Langguth</em>, <em>ICA</em>, and other brands under contract.
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    <strong>Standard Rate (VT):</strong> If no maintenance contract is linked, the standard 'VT' rate is used.
                  </List.Item>
                </List>

                <p>
                  <strong>2. Time & Attendance</strong>
                </p>
                <List type="bullet">
                  <List.Item>
                    <strong>Standard:</strong> "Start Technician" to "End Technician" (includes travel time).
                  </List.Item>
                  <List.Item>
                    <strong>Horeca:</strong> "Planned Start" to "Planned End" (excludes travel time).
                  </List.Item>
                </List>

                <p>
                  <strong>3. Overtime Rules</strong>
                </p>
                <List type="bullet">
                  <List.Item>
                    <strong>Mon - Thu:</strong> Normal rate up to 9h. Excess is 150%.
                  </List.Item>
                  <List.Item>
                    <strong>Friday:</strong> Normal rate up to 8h. Excess is 150%.
                  </List.Item>
                  <List.Item>
                    <strong>Weekend:</strong> Saturday (150%) and Sunday (200%).
                  </List.Item>
                </List>

                <p>
                  <strong>4. Travel Costs</strong>
                </p>
                <List type="bullet">
                  <List.Item>
                    <strong>Standard:</strong> Charged per kilometer driven.
                  </List.Item>
                  <List.Item>
                    <strong>Horeca:</strong> Fixed daily fee (Forfait). Weekday base rate (€95), Saturday (150%), Sunday (200%).
                  </List.Item>
                </List>
              </TextContainer>
            </div>
          </div>
        )}
      </Modal.Section>
    </Modal>
  );
}
